import React from 'react';
import ConfiguredItemArt from '../components/configurator/ConfiguredItemArt';
import {ProductVisual} from '../components/ProductMedia';
import {productMedia} from '../utils/productMedia';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Trash2, Minus, Plus, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { useCart } from '../contexts/CartContext';
import { FREE_SHIPPING_THRESHOLD } from '../lib/shop';
import { INTERNATIONAL_SHIPPING_RATE } from '../lib/shipping';
import { useSEO } from '../utils/seo';
import { ConfigurationSummary, isMadeToOrder } from '../utils/orderPresentation';
import { readSession } from '../utils/checkout';
import { characterName } from '../lib/characters';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import './AnnexesAtelier.css';

const COPY = {
  fr: {
    seoTitle: 'Mon Placard — Le Placard à Doudous', seoDescription: 'Votre sélection de marque-pages en tissu.',
    eyebrow: 'Votre sélection', title: 'Mon Placard',
    pending: 'Vous revenez du paiement. Votre sélection et vos informations de livraison ont été conservées. Si votre paiement est en attente, vérifiez sa confirmation avant de recommencer.',
    checkPayment: 'Vérifier le paiement', empty: 'Votre placard est vide', emptyText: 'Découvrez les modèles ou composez votre marque-page.', browse: 'Découvrir la boutique',
    view: name => `Voir ${name}`, remove: name => `Retirer ${name} du panier`, decrease: name => `Diminuer la quantité de ${name}`, increase: name => `Augmenter la quantité de ${name}`,
    onlyOne: 'Pièce unique — il n’en existe qu’une', summary: 'Récapitulatif', subtotal: n => `Sous-total (${n} article${n > 1 ? 's' : ''})`,
    shipping: 'Livraison en France', free: 'Offerte', abroad: amount => `Hors de France : ${amount}`, untilFree: amount => `Plus que ${amount} pour la livraison offerte`, total: 'Total', checkout: 'Commander', continue: 'Continuer la boutique',
  },
  en: {
    seoTitle: 'My Cupboard — Le Placard à Doudous', seoDescription: 'Your selection of fabric bookmarks.',
    eyebrow: 'Your selection', title: 'My Cupboard',
    pending: 'You’re back from payment. Your selection and delivery details have been saved. If your payment is pending, check its confirmation before trying again.',
    checkPayment: 'Check the payment', empty: 'Your cupboard is empty', emptyText: 'Discover the companions and find your bookmark.', browse: 'Discover the shop',
    view: name => `View ${name}`, remove: name => `Remove ${name} from your bag`, decrease: name => `Decrease the quantity of ${name}`, increase: name => `Increase the quantity of ${name}`,
    onlyOne: 'One of a kind — there is only one', summary: 'Summary', subtotal: n => `Subtotal (${n} item${n > 1 ? 's' : ''})`,
    shipping: 'Shipping to France', free: 'Free', abroad: amount => `Outside France: ${amount}`, untilFree: amount => `Only ${amount} more for free shipping`, total: 'Total', checkout: 'Checkout', continue: 'Continue shopping',
  },
};

export default function Cart() {
  const c = useCopy(COPY);
  const { locale, lp, money } = useLocale();
  useSEO(c.seoTitle, c.seoDescription);
  const { items, removeItem, updateQty, total, shipping, grandTotal, count } = useCart();
  const pendingCheckout = readSession('lpad_checkout_pending');

  return (
    <div className="annexes-atelier annexes-cart min-h-screen pt-32 sm:pt-36">
      <div className="annexes-container max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 pb-20">
        <span className="kicker">{c.eyebrow}</span>
        <h1 className="rubrique-title mb-12">{c.title}</h1>
        {pendingCheckout?.sessionId && items.length > 0 && (
          <p role="status" className="annexes-notice mb-6 rounded-xl bg-secondary/60 p-4 font-sans text-sm text-foreground">
            {c.pending}{' '}
            <Link className="underline" to={lp(`/commande-confirmee?session_id=${encodeURIComponent(pendingCheckout.sessionId)}`)}>{c.checkPayment}</Link>
          </p>
        )}

        {items.length === 0 ? (
          <div className="annexes-empty py-20 text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-secondary">
              <ShoppingBag className="h-9 w-9 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <p className="font-serif text-2xl text-foreground mb-3">{c.empty}</p>
            <p className="font-sans text-muted-foreground mb-8">{c.emptyText}</p>
            <Button asChild className="annexes-action rounded-full bg-primary px-7 font-sans font-medium text-primary-foreground shadow-soft transition-[box-shadow,transform] duration-200 hover:shadow-ambient active:scale-[0.98]">
<Link to={lp('/boutique')}>
                {c.browse}
              </Link>
</Button>
          </div>
        ) : (
          <div className="annexes-cart-layout grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-16">
            {/* Items — liste hairline */}
            <div className="annexes-cart-items lg:col-span-2">
              {items.map(cartItem => ({ ...cartItem, displayName: characterName(cartItem.slug, cartItem.name, locale) })).map((item, idx) => (
                <motion.div
                  key={item.cartKey || `${item.slug}-${item.color}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={`annexes-cart-item flex gap-3 sm:gap-5 py-6 ${idx > 0 ? 'border-t border-border' : ''}`}
                >
                  <Link to={lp(`/produit/${item.slug}`)} className="shrink-0" aria-label={c.view(item.displayName)}>
                    <div className="annexes-cart-media h-24 w-20 overflow-hidden rounded-xl bg-secondary transition-opacity duration-200 hover:opacity-85">
                      {item.configuration ? <ConfiguredItemArt item={item} />
                        : <ProductVisual media={productMedia({...item,images:item.image ? [item.image] : []}, {locale})[0]} thumbnail />}
                    </div>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 break-words">
                        <Link to={lp(`/produit/${item.slug}`)}>
                          <p className="font-serif text-base text-foreground transition-colors duration-200 hover:text-[hsl(var(--brand-fuchsia-ink))]">{item.displayName}</p>
                        </Link>
                      </div>
                      <button type="button" onClick={() => removeItem(item.slug, item.color, item.cartKey)} aria-label={c.remove(item.displayName)}
                        className="flex h-11 w-11 flex-shrink-0 items-center justify-center text-muted-foreground transition-colors duration-200 hover:text-destructive">
                        <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                      </button>
                    </div>
                    <ConfigurationSummary configuration={item.configuration} className="sm:grid sm:grid-cols-2 sm:gap-2 sm:space-y-0" />
                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => updateQty(item.slug, item.color, item.quantity - 1, item.cartKey)}
                          aria-label={c.decrease(item.displayName)}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-border transition-colors duration-200 hover:bg-secondary"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-5 text-center font-sans text-sm font-medium">{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQty(item.slug, item.color, item.quantity + 1, item.cartKey)}
                          aria-label={c.increase(item.displayName)}
                          disabled={!isMadeToOrder(item) && item.quantity >= 1}
                          title={!isMadeToOrder(item) ? c.onlyOne : undefined}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-border transition-colors duration-200 hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="font-sans text-base font-semibold text-foreground">{money(item.price * item.quantity)}</span>
                    </div>
                    {!isMadeToOrder(item) && <p className="mt-2 font-sans text-xs text-muted-foreground">{c.onlyOne}</p>}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Summary */}
            <div>
              <div className="annexes-card annexes-cart-summary sticky top-28 rounded-2xl bg-card p-6 shadow-soft">
                <h2 className="mb-5 font-serif text-lg text-foreground">{c.summary}</h2>
                <div className="mb-5 space-y-3">
                  <div className="flex justify-between font-sans text-sm">
                    <span className="text-muted-foreground">{c.subtotal(count)}</span>
                    <span>{money(total)}</span>
                  </div>
                  <div className="flex justify-between font-sans text-sm">
                    <span className="text-muted-foreground">{c.shipping}</span>
                    <span className={shipping === 0 ? 'font-medium text-[hsl(var(--brand-fuchsia-ink))]' : ''}>
                      {shipping === 0 ? c.free : money(shipping)}
                    </span>
                  </div>
                  {shipping > 0 && <p className="font-sans text-xs text-muted-foreground">{c.abroad(money(INTERNATIONAL_SHIPPING_RATE))}</p>}
                  {shipping > 0 && (
                    <p className="font-sans text-xs text-[hsl(var(--brand-fuchsia-ink))]">
                      {c.untilFree(money(FREE_SHIPPING_THRESHOLD - total))}
                    </p>
                  )}
                </div>
                <Separator className="mb-4" />
                <div className="mb-6 flex justify-between font-sans text-base font-semibold">
                  <span>{c.total}</span>
                  <span className="text-foreground">{money(grandTotal)}</span>
                </div>
                <Button asChild className="annexes-action min-h-11 w-full rounded-full bg-primary font-sans font-medium text-primary-foreground shadow-soft transition-[box-shadow,transform] duration-200 hover:shadow-ambient active:scale-[0.98]" data-testid="cart-page-checkout-button">
<Link to={lp('/checkout')}>
                    {c.checkout} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
</Button>
                <Link to={lp('/boutique')} className="annexes-quiet-link mt-4 block text-center font-sans text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground">
                  {c.continue}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
